<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";
import CustomerSearchDialog from "./CustomerSearchDialog.vue";
import CheckoutModal from "./CheckoutModal.vue";
import { posStore } from "../../../../../stores/posStore";
import AsyncImage from "../../../../../components/ui/form/AsyncImage.vue";
import {
  formatCurrency,
  formatNumber,
  isEmpty,
  printPDFBlob,
} from "../../../../../utils/CommonFunctions";
import IconComponent from "../../../../../components/ui/common/IconComponent.vue";
import NumberInput from "../../../../../components/ui/form/NumberInput.vue";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";
import {
  CashierDuty,
  Customer,
  DeliveryTracking,
  ItemCategory,
  ItemGroup,
  PaymentType,
  PosStation,
  RecordStatus,
  RestaurantTables,
  SalesDocument,
  SalesRep,
} from "../../../../../types/models";
import DropDownButton from "../../../../../components/ui/common/DropDownButton.vue";
import { User } from "../../../../../types/auth";
import MessageBox from "../../../../../services/MessageBox";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";
import CustomersForm from "../../../masterdata/customers/CustomersForm.vue";
import CustomerAddressForm from "../../../masterdata/customers/address/CustomerAddressForm.vue";
import Buttons from "../../../../../components/ui/form/Buttons.vue";
import DataTablePagination from "../../../../../components/table/DataTablePagination.vue";

const props = defineProps<{
  payment_types: PaymentType[];
  item_categories: ItemCategory[];
  item_groups: ItemGroup[];
  delivery_staffs: User[];
  posStation: PosStation | undefined;
  cashierDuty: CashierDuty | null;
  salesReps: SalesRep[];
}>();

const emit = defineEmits<{
  (e: "new"): void;
  (e: "back"): void;
  (e: "order", table: RestaurantTables): void;
  (e: "order-created", tables: RestaurantTables[]): void;
  (e: "view-bill", table: RestaurantTables): void;
}>();

const pos = posStore();
const activeTab = ref("order");
//const customerSearch = ref("");
const showCustomerList = ref(false);
const customerList = ref([]);
const pendingDeliveries = ref<DeliveryTracking[]>([]);
const endPoint = "revenue/sales/pos/";

defineExpose({
  triggerCheckout() {
    if (
      !pos.showCheckoutModal &&
      (pos.selectedPosInvoice?.invoice.items?.length ?? 0) > 0
    ) {
      pos.showCheckoutModal = true;
    }
  },
});

const tabs = ref([
  { id: "order", label: props.posStation?.outlet?.type == 'Shop' ? "Current Order" : 'Items', icon: "ShoppingCart", visible: true },
  { id: "deliveries", label: "Deliveries", icon: "Truck", badge: "", visible: props.posStation?.outlet?.type == 'Shop' },
  { id: "notifications", label: "Notifications", icon: "Bell", badge: "", visible: props.posStation?.outlet?.type == 'Shop' },
]);

// Sample data for pending deliveries

// Sample data for notifications
const notifications = [
  {
    id: 1,
    icon: "🔔",
    title: "New Order",
    message: "Order #1234 has been placed",
    time: "5 minutes ago",
  },
  {
    id: 2,
    icon: "⚠️",
    title: "Low Stock Alert",
    message: "Item XYZ is running low on stock",
    time: "1 hour ago",
  },
  {
    id: 3,
    icon: "💰",
    title: "Payment Received",
    message: "Payment for Order #1233 received",
    time: "2 hours ago",
  },
  {
    id: 4,
    icon: "🚚",
    title: "Delivery Update",
    message: "Order #1232 has been delivered",
    time: "3 hours ago",
  },
  {
    id: 5,
    icon: "📦",
    title: "Stock Update",
    message: "New inventory has been added",
    time: "4 hours ago",
  },
];

const showCustomerForm = ref(false);
const handleNoCustomer = () => {
  if (pos.customerSearch.length >= 3) {
    MessageBox.confirm({
      title: "No Customer Found",
      message: "Do you want to create a new customer?",
      confirmText: "Create New Customer",
      cancelText: "Cancel",
      type: "info",
    }).then((result) => {
      if (result) {
        customerMode.value = "create";
        showCustomerForm.value = true;
      }
    });
  } else {
    Toast.show("Please enter at least 3 characters to search for a customer", "warning");
  }
};

const handleCustomerCreated = (data: any) => {  
  if (customerMode.value === "edit") {
    pos.setCustomer(data);    
  } else {
    const mob = data.mobile.split('-')[1];
    pos.customerSearch = mob;
    handleCustomerSearch();
  }
 nextTick(() => {
     showCustomerForm.value = false;
  pos.customerSearch = "";
  });
}
const handleCustomerSearch = async () => {
  if (pos.customerSearch.length >= 3) {
    const response = await api.post(endPoint + "customers", {
      search_query: pos.customerSearch,
      details: true,
    });
    if (response.success == true) {
      if (response.data.length > 1) {
        customerList.value = response.data;
        showCustomerList.value = true;
      } else if (response.data.length == 1) {
        pos.setCustomer(response.data[0]);
      } else {
        handleNoCustomer();
      }
    } else {
      Toast.show("Could not retrieve customers", "warning");
    }
  }
};

const handleStateChanged = () => {
  delivery_citys_id.value = "";
  getPendingDeliveries();
};
const handleCityChanged = () => {
  delviery_states_id.value = "";
  getPendingDeliveries();
};

const deliveryPageChange = (page: number) => {
  deliveryCurrentPage.value = page;
  getPendingDeliveries();
};

const refreshDeliverys = () => {
  deliveryCurrentPage.value = 1;
  getPendingDeliveries();
};
const getPendingDeliveries = async () => {
  const response = await api.post(endPoint + "deliverys", {
    search_query: deliverySearchQuery.value,
    states_id: delviery_states_id.value,
    citys_id: delivery_citys_id.value,
    per_page: 10,
    page: deliveryCurrentPage.value,
    details: true,
  }, {skipLoading: true});
  if (response.success == true) {
    setPendingDeliveries(response);
  } else {
    Toast.show("Could not retrieve Pending Delivery list", "warning");
  }
};

const handleCreateQuotation = async () => {
  let invoice: SalesDocument =
    pos.selectedPosInvoice?.invoice ?? ({} as SalesDocument);
  invoice.to_deliver = false ;//delivery_staff ? true : false;
  //invoice.delivery_staffs_id = delivery_staff;
  invoice.warehouses_id = props.posStation?.outlet?.warehouses_id ?? "";
  invoice.outlets_id = props.posStation?.outlet?.id ?? "";
  invoice.outlet_pos_stations_id = props.posStation?.id ?? "";
  invoice.price_lists_id = props.posStation?.outlet?.price_lists_id ?? "";
  invoice.sales_channels_id = props.posStation?.outlet?.sales_channels_id ?? "";

  invoice.share = true;

  if (
    !invoice.delivery_address_id &&
    invoice.customer?.addresses &&
    invoice.customer.addresses.length > 0
  ) {
    invoice.delivery_address_id = invoice.customer?.addresses?.[0]?.id ?? "";
  }

  invoice.cashier_dutys_id = props.cashierDuty ? props.cashierDuty.id : "";
  
  const response = await api.post(endPoint + "quotation",  invoice);
  if (response.success == true) {
    pos.voidInvoice();
    printPDFBlob(response.data)
  } else {
    Toast.show("Could not Create Quotation", "warning");
  }
}

const handleCustomerSelect = (customer: Customer) => {
  pos.setCustomer(customer);
  showCustomerList.value = false;
  pos.customerSearch = "";
};

//const discount = ref(0);

const deliveryCurrentPage = ref(1);
const deliveryTotalPages = ref(1);
const deliverySearchQuery = ref("");
const delivery_users = ref<User[]>([]);
const record_status = ref<RecordStatus[]>([]);
const delivery_citys_id = ref("");
const delviery_states_id = ref("");
const states = ref<{ id: any; name: any }[]>([]);
const citys = ref<{ id: any; name: any }[]>([]);

const setPendingDeliveries = (response: any) => {
  pendingDeliveries.value = response.data.data;
  deliveryTotalPages.value = response.data.total;
  if (response.lists && response.lists.status) {
    record_status.value = response.lists.status;
  }
  if (response.lists && response.lists.users)
    delivery_users.value = response.lists.users;
  let tab = tabs.value.find((tab) => tab.id == "deliveries");
  if (tab) {
    tab.badge = (pendingDeliveries.value.length ?? "").toString();
  }
  if (!delviery_states_id.value && !delivery_citys_id.value) {
    const uniqueSet = new Set();
    states.value = pendingDeliveries.value
      .filter((item: any) => {
        const uniqueIdName = `${item.states_id}-${item.state}`;
        if (!uniqueSet.has(uniqueIdName) && !isEmpty(item.state)) {
          uniqueSet.add(uniqueIdName);
          return true;
        }
        return false;
      })
      .map((item: any) => ({
        id: item.states_id,
        name: item.state,
      }));
    citys.value = pendingDeliveries.value
      .filter((item: any) => {
        const uniqueIdName = `${item.citys_id}-${item.city}`;
        if (!uniqueSet.has(uniqueIdName) && !isEmpty(item.city)) {
          uniqueSet.add(uniqueIdName);
          return true;
        }
        return false;
      })
      .map((item: any) => ({
        id: item.citys_id,
        name: item.city,
      }));
  }
};

const assignDeliveryStaff = async (delivery: DeliveryTracking, user: User) => {
  const params = {
    id: delivery.uid,
    users_id: user.uid,
    page: deliveryCurrentPage.value,
    per_page: 25,
    search_query: deliverySearchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "assign-user",
    params
  );
  if (response.success == true) {
    setPendingDeliveries(response);
  }
};

const updateDeliveryStatus = async (
  delivery: DeliveryTracking,
  status: RecordStatus
) => {
  let attachment = null;
  let remarks = null;
  const confirmed = await MessageBox.confirm({
    title: "Update Delivery",
    message: status.require_attachment
      ? "Please upload the Delivery Confimration"
      : "Are you sure you want to update the delivery status?",
    confirmText: "Update as " + status.name,
    requireRemarks: status.require_remarks,
    requireAttachments: status.require_attachment,
    showRemarks: true,
    cancelText: "Cancel",
    type: "info",
  });
  if (confirmed) {
    attachment = confirmed.attachment;
    remarks = confirmed.remarks;
    if (status.require_attachment && !attachment) return;
    if (status.require_remarks && !remarks) return;
  } else {
    return;
  }

  const params = {
    id: delivery.uid,
    delivery_status_id: status.id,
    attachment: attachment,
    remarks: remarks,
    page: deliveryCurrentPage.value,
    per_page: 25,
    search_query: deliverySearchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "update-delivery",
    params
  );
  if (response.success == true) {
    setPendingDeliveries(response);
  }
};

const getPendingDeliveryStatus = (delivery: DeliveryTracking) => {
  let status = record_status.value.find(
    (e) => e.linked_index !== undefined && e.linked_index >= 1
  );
  let res = record_status.value.filter(
    (e) => e.id > (delivery.status_id ?? 0) && e.name !== "Delivery Assigned"
  );
  return status && !isEmpty(status)
    ? res.filter((e) => e.linked_index != status.linked_index)
    : res;
};

const showAddNewAddress = ref(false)

const searchCustomerInput = ref<HTMLInputElement | null>(null);
const focusCustomerSearch = () => {
  searchCustomerInput.value?.focus();
};

onMounted(() => {
  window.addEventListener("keydown", handleKeyPress, true);
  //getPendingDeliveries();
  setInterval(getPendingDeliveries, 300000); // 300000 ms = 5 minutes
});

const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === "F3") {
    event.preventDefault();
    showCustomerList.value = true;
  } else if (event.key === "F4") {
    event.preventDefault();
    focusCustomerSearch();
  } else if (event.key === "F12") {
    event.preventDefault();
    if ((pos.selectedPosInvoice?.invoice.items?.length ?? 0) > 0) {
      pos.showCheckoutModal = true;
    }
  } else if (event.key === "F11") {
    event.preventDefault();
   createRestaurantOrder
  } else if (event.key === "Escape") {
    event.preventDefault();
    restaurantBack();
  }
};

const customerCreated = (address: any[]) => {
  pos.selectedPosInvoice?.invoice?.customer && (pos.selectedPosInvoice.invoice.customer.addresses = address);
}

const createRestaurantOrder = async () => {
  const params = {
    outlets_id: props.posStation?.outlet?.uid ?? "",
    outlet_pos_stations_id: props.posStation?.uid ?? "",
    tables_id: pos.selectedTable?.uid ?? "",
    orders_id: pos.selectedTable?.orders ? pos.selectedTable?.orders.uid : "",
    items: pos.selectedPosInvoice?.invoice.items ?? [],
    customers_id: pos.selectedPosInvoice?.invoice.customer?.uid ?? "",
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "order",
    params
  );
  if (response.success == true) {
    emit("order-created", response.data);
  }
}


const viewBill = () => {
  emit("view-bill", pos.selectedTable!);
}

const restaurantBack = () => {
  emit('back');
}

watch(
  () => pos.showCheckoutModal,
  () => {
    if (pos.showCheckoutModal) {
      window.removeEventListener("keydown", handleKeyPress);
    } else {
      window.window.addEventListener("keydown", handleKeyPress, true);
    }
  }
);

const customerMode = ref<"create" | "edit">("create");
const editCustomer = ref<Customer | null>(null);
const handleEditSelect = (customer: Customer) => {
  editCustomer.value = customer;
  customerMode.value = "edit";
  showCustomerList.value = false;
  showCustomerForm.value = true;
  pos.customerSearch = "";
};

</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Customer Search -->
    <div class="p-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <div class="relative flex-1">
          <IconComponent
            name="UserRoundSearch"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            v-model="pos.customerSearch"
            ref="searchCustomerInput"
            type="search"
            placeholder="Search customer... (F4)"
            @keyup.enter="handleCustomerSearch"
            class="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
          />
        </div>
        <button
          @click="showCustomerList = true"
          class="p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <IconComponent name="FileUser" class="w-5 h-5" />
        </button>
      </div>

      <!-- Selected Customer -->
      <div
        v-if="
          pos.selectedPosInvoice?.invoice.customer &&
          pos.selectedPosInvoice?.invoice.customers_id
        "
        class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/50 rounded-lg"
      >
        <div class="flex justify-between items-start">
          <div>
            <h4 class="font-medium text-gray-900 dark:text-white">
              {{ pos.selectedPosInvoice?.invoice.customer.name }}
            </h4>
          </div>
          <div>
            <button
            @click="handleEditSelect(pos.selectedPosInvoice?.invoice.customer)"
            class="text-orange-400 hover:text-orange-500 dark:hover:text-orange-300"
          >
            <IconComponent name="Pencil" />
          </button>
          <button
            @click="pos.clearCustomer()"
            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <IconComponent name="Trash2" />
          </button>
          </div>
        </div>
        <div class="md:grid md:grid-cols-3 md:gap-4">
                <div class="text-sm text-gray-600 dark:text-gray-400">
                  {{ pos.selectedPosInvoice?.invoice.customer.mobile }}
                </div>
                <div class="text-neutral-600 text-sm font-semibold text-right col-span-2">
                  <span
                    v-if="Number(pos.selectedPosInvoice?.invoice.customer?.credit_limit) - Number(pos.selectedPosInvoice?.invoice.customer?.outstanding) < 0"
                    class="inline-block px-2 py-1 rounded-full bg-red-100 text-red-700"
                  >
                    Credit Balance: {{ formatNumber(Number(pos.selectedPosInvoice?.invoice.customer?.credit_limit) - Number(pos.selectedPosInvoice?.invoice.customer?.outstanding)) }}
                  </span>
                  <span
                    v-else
                    class="inline-block px-2 py-1 rounded-full bg-blue-100 text-blue-700"
                  >
                    Credit Balance: {{ formatNumber(Number(pos.selectedPosInvoice?.invoice.customer?.credit_limit) - Number(pos.selectedPosInvoice?.invoice.customer?.outstanding)) }}
                  </span>
                </div>
              </div>
        <div class="flex mt-2  justify-between items-start">
            <IconComponent name="House" />
            <p class="text-sm text-gray-600 dark:text-gray-400 ml-1 mt-1">
              <span
              v-if="pos.selectedPosInvoice?.invoice?.customer?.selectedAddress"
              >
              {{
                pos.selectedPosInvoice?.invoice.customer.selectedAddress?.full_address ?? ""
              }}
              </span>
            </p>
            <DropDownButton :hide-arrow="true" v-if="(pos.selectedPosInvoice?.invoice?.customer?.addresses?.length ?? 0) > 1" icon="EllipsisVertical">
              <Buttons v-for="address in pos.selectedPosInvoice?.invoice?.customer?.addresses" :key="address.id" @click="pos.setCustomerAddress(address)" 
              :caption="address.full_address" class-name="text-left border-b" />
            </DropDownButton>
            <button
              @click="showAddNewAddress = true"
              class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <IconComponent name="HousePlus" />
            </button>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700">
      <div class="flex">
        <template v-for="tab in tabs" :key="tab.id">
        <button
          :key="tab.id"
          v-if="tab.visible"
          @click="activeTab = tab.id"
          class="flex-1 px-4 py-3 text-sm font-medium text-center relative"
          :class="[
            activeTab === tab.id
              ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
          ]"
        >
          <div class="flex flex-col items-center">
            <IconComponent :name="tab.icon" class="block text-lg mb-1" />
            {{ tab.label }}
          </div>
          <span
            v-if="tab.badge"
            class="absolute top-2 right-2 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-medium text-white bg-red-500 rounded-full"
          >
            {{ tab.badge }}
          </span>
        </button>
        </template>
      </div>
    </div>

    <!-- Content Area -->
    <div class="flex-1 flex flex-col min-h-0">
      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto">
        <!-- Order Tab -->
        <div v-if="activeTab === 'order'" class="p-4">
          <div
            v-if="pos.selectedPosInvoice?.invoice.items.length === 0"
            class="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400"
          >
            <span class="text-4xl mb-2">🛒</span>
            <p>Cart is empty</p>
          </div>
          <div v-else class="space-y-2">
            <template
              v-for="item in pos.selectedPosInvoice?.invoice.items"
              :key="item.id"
            >
              <div
                class="flex items-center gap-4 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg relative"
                @mouseenter="item.isHovered = true"
                @mouseleave="item.isHovered = false"
                tabindex="0"
              >
                <div
                  class="w-10 h-10 flex items-center justify-center text-2xl flex-shrink-0"
                >
                  <AsyncImage
                    :src="item.photo"
                    :alt="item.name"
                    className="w-10 h-10 object-cover rounded-lg"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div>
                    <h4
                      class="font-medium text-gray-900 dark:text-white truncate"
                    >
                      {{ item.name }}
                    </h4>
                  </div>
                  <div class="grid grid-cols-4 gap-2 flex-shrink-0">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ formatNumber(item.price) }}
                    </p>
                    <NumberInput
                        v-model="item.quantity"
                        @input="pos.updateQuantity(item, item.quantity)"
                        @change="pos.updateQuantity(item, item.quantity)"
                        class="w-full col-span-2 text-right"
                        size="sm"
                        :show-controls="true" />
                    <div class="text-right min-w-[80px]">
                      {{ formatNumber(item.net_total ?? 0) }}
                    </div>
                    <div class="col-span-3" v-if="item.isExpanded">
                      <div class="grid grid-cols-2">
                        <div>
                          <NumberInput
                            v-model="item.quantity"
                            @input="pos.updateQuantity(item, item.quantity)"
                            @change="pos.updateQuantity(item, item.quantity)"
                            class="w-10 text-right"
                            size="sm"
                            max-width="100px"
                            :show-controls="false"
                          />
                          <p
                            class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                          >
                            Quantity
                          </p>
                        </div>
                        <div>
                          <NumberInput
                            v-model="item.discount_p"
                            @input="pos.updateLineTotal(item)"
                            class="w-10 text-right"
                            size="sm"
                            max-width="100px"
                            :show-controls="false"
                          />
                          <p
                            class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                          >
                            Discount %
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  @click="item.isExpanded = !item.isExpanded"
                  ref="expand-button"
                  class="absolute top-2 right-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <IconComponent
                    :name="
                      item.isExpanded ? 'CircleArrowUp' : 'CircleArrowDown'
                    "
                    class="text-red"
                  />
                </button>
                <button
                  v-show="item.isHovered"
                  @click="pos.removeItem(item)"
                  ref="remove-button"
                  class="absolute top-2 right-8 text-gray-400 hover:text-gray-500 dark:hover:text-red-500"
                >
                  <IconComponent name="Trash2" class="text-red" />
                </button>
              </div>
            </template>
          </div>
        </div>

        <!-- Pending Deliveries Tab -->
        <div v-else-if="activeTab === 'deliveries'" class="p-4">
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <SearchSelect
                label="State"
                @select="handleStateChanged"
                v-model="delviery_states_id"
                :options="states"
                :remote="false"
              />
              <SearchSelect
                label="City"
                @select="handleCityChanged"
                v-model="delivery_citys_id"
                :options="citys"
                :remote="false"
              />
            </div>
            <div
              v-for="delivery in pendingDeliveries"
              :key="delivery.id"
              class="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div class="flex justify-between items-start mb-2">
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ delivery.related_document_no }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ delivery.customer_name }}
                  </p>
                </div>
                <div class="text-right">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :style="{ backgroundColor: delivery.status_color }"
                  >
                    {{ delivery.status }}
                  </span>
                  <br />
                  <a :href="'tel:' + delivery.customer_mobile">{{
                    delivery.customer_mobile
                  }}</a>
                </div>
              </div>
              <div
                class="flex justify-between text-sm items-center mt-2 overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {{ delivery.full_address }}
              </div>
              <div class="flex justify-between items-center mt-2">
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  <div
                    class="flex items-center gap-2"
                    >
                  <div
                    class="flex items-center gap-2"
                    >
                    <DropDownButton
                      :hideArrow="true"
                    >
                      <button
                        @click="updateDeliveryStatus(delivery, status)"
                        v-for="status in getPendingDeliveryStatus(delivery)"
                        class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <IconComponent :name="status.icon_css" />
                        {{ status.name }}
                      </button>
                    </DropDownButton>
                    <template v-if="!isEmpty(delivery.assigned_to_user)">
                    <IconComponent name="UsersRound" />
                    <span>{{ delivery.assigned_to_user }}</span>
                    </template>
                  </div>
                  <DropDownButton :hideArrow="true" icon="UsersRound" v-if="isEmpty(delivery.assigned_to_user)">
                    <button
                      @click="assignDeliveryStaff(delivery, user)"
                      v-for="user in delivery_users"
                      class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {{ user.name }}
                    </button>
                  </DropDownButton>
                  </div>
                </span>
                <span class="font-medium text-gray-900 dark:text-white">
                  {{ delivery.delivery_date }}
                </span>
              </div>
            </div>
          </div>
          <div>
            <DataTablePagination :current-page="deliveryCurrentPage" :total-pages="deliveryTotalPages" @pageChange="deliveryPageChange" />
          </div>
        </div>

        <!-- Notifications Tab -->
        <div v-else-if="activeTab === 'notifications'" class="p-4">
          <div class="space-y-4">
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div class="flex gap-4">
                <div
                  class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-xl"
                >
                  {{ notification.icon }}
                </div>
                <div class="flex-1">
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ notification.title }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ notification.message }}
                  </p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {{ notification.time }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Bottom Section -->
      <div
        v-if="activeTab === 'order'"
        class="mt-auto border-t border-gray-200 dark:border-gray-700"
      >
        <!-- Order Summary -->
        <div class="p-4 space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
            <span class="font-medium text-gray-900 dark:text-white">{{
              formatCurrency(pos.sub_total ?? 0)
            }}</span>
          </div>
          <div class="flex justify-between items-center text-sm">
            <span class="text-gray-600 dark:text-gray-400">Discount</span>
            <NumberInput
              v-model="pos.discount"
              class="w-10 text-right"
              :readonly="!posStation?.outlet?.max_discount || !posStation?.outlet?.max_discount == null || posStation?.outlet?.max_discount <= 0"
              size="sm"
              max-width="100px"
              :show-controls="false"
              @input="pos.applyDiscount(pos.discount)"
            />
            <span class="font-medium text-gray-900 dark:text-white"
              >-{{ formatCurrency(pos.discount_amount ?? 0) }}</span
            >
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Before Tax</span>
            <span class="font-medium text-gray-900 dark:text-white">{{
              formatCurrency(pos.value_before_tax ?? 0)
            }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Tax</span>
            <span class="font-medium text-gray-900 dark:text-white">{{
              formatCurrency(pos.tax_amount ?? 0)
            }}</span>
          </div>
          <div
            class="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200 dark:border-gray-700"
          >
            <span class="text-gray-900 dark:text-white">Total</span>
            <span class="text-gray-900 dark:text-white">{{
              formatCurrency(pos.net_total ?? 0)
            }}</span>
          </div>
        </div>

        <!-- Checkout Button -->
        <div class="pl-4 pr-4 bg-gray-50 dark:bg-gray-900 flex gap-2">
          <button
            @click="createRestaurantOrder"
            class="w-full py-3 px-4 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="pos.selectedPosInvoice?.invoice.items.length === 0"
          >
            Order (F11)
          </button>
          <button
            @click="viewBill"
            v-if="posStation?.outlet?.type == 'Restaurant'"
            class="w-full py-3 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!pos.selectedTable?.orders"
          >
            Bill (F12)
          </button>
          <button            
            @click="pos.showCheckoutModal = true"
            v-else
            class="w-full py-3 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="pos.selectedPosInvoice?.invoice.items.length === 0"
          >
            Checkout (F12)
          </button>
          <button
            @click="restaurantBack"
            class="w-full py-3 px-4 bg-orange-300 text-black dark:text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back (Esc)
          </button>
        <DropDownButton :hideArrow="true" icon="FileText" text="Select" v-if="posStation?.outlet?.type == 'Shop'"
        className="w-full py-3 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed" 
        :disabled="pos.selectedPosInvoice?.invoice.items.length === 0">
            <Buttons
              class="w-full px-1 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              :disabled="!pos.selectedPosInvoice?.invoice.customer"
               @click="handleCreateQuotation"
              caption="Create Quotation"
            />
          </DropDownButton>
        </div>
      </div>
    </div>

    <!-- Customer List Dialog -->
    <CustomerSearchDialog
      v-if="showCustomerList"
      :customers="customerList"
      @select="handleCustomerSelect"
      @edit="handleEditSelect"
      @close="showCustomerList = false"
    />

    <!-- Checkout Modal -->
    <CheckoutModal
      v-if="pos.showCheckoutModal"
      :pos-station="posStation"
      :payment_types="payment_types"
      :cashierDuty="cashierDuty"
      :salesReps="props.salesReps"
      @getDeliverys="refreshDeliverys"
      :delivery_staffs="delivery_staffs"
      @new="$emit('new')"
      :total="pos.net_total ?? 0"
      @close="pos.showCheckoutModal = false"
    />

    <CustomersForm
      v-if="showCustomerForm"
      end-point="/clients/revenue/master-data/customers/"
      :ignore-permission="true"
      :minimized="true"
      :data="editCustomer"
      :listCreateName="pos.customerSearch"
      title="Customers"
      :mode="customerMode"
      @save="handleCustomerCreated"
      @close="showCustomerForm = !showCustomerForm"
    />

    <CustomerAddressForm v-if="showAddNewAddress" @close="showAddNewAddress = false" title="New Address" mode="create"
    :posStation="posStation" @save="customerCreated"
    end-point="/clients/revenue/master-data/customers/" :parent="pos.selectedPosInvoice?.invoice.customer" :ignore-permission="true" />
  </div>
</template>
